/**
 * @fileoverview Livechat utility functions for display, formatting, and data management.
 * Provides helpers for chat styling, user identification, timestamps, and state management.
 */

import { InteractionType, LivechatSessionType } from "@/store/modules/livechat/types";
import store from "@/store";
import _ from "lodash";
import moment from "moment";
import { v4 as uuid } from "uuid";
import { LivechatType } from "@/store/modules/livechat/mutations";

/**
 * Determines CSS class for a chat item based on its state and agent assignment.
 *
 * @param {number} index - Chat index in list
 * @param {Object} chat - Chat object with status properties
 * @param {string} selectedChatId - Currently selected chat ID
 * @param {string} [partitionKey] - Optional partition key for comparison
 * @returns {string} CSS class string for the chat item
 *
 * @example
 * const className = getChatClass(0, chat, selectedId);
 */
export function getChatClass(
  index: number,
  {
    PartitionKey,
    RowKey,
    agents,
    resolved,
    unreadMessage,
    user_id,
  }: {
    PartitionKey: string;
    RowKey: string;
    agents: string[];
    resolved: boolean;
    unreadMessage: number;
    user_id?: string;
  },
  selectedChatId: string,
  partitionKey?: string
) {
  let className;
  let isActiveChat;
  const agentEmail = _.get(store, "state.profile.email");
  const isInChat = agents && agents.indexOf(agentEmail) !== -1;
  const hasAgentChat = agents && agents.length > 0;
  const isResolved = resolved === true;
  const hasUnreadMessages = unreadMessage > 0;
  if (partitionKey) {
    isActiveChat = user_id === selectedChatId && PartitionKey === partitionKey;
  } else {
    isActiveChat = RowKey === selectedChatId;
  }
  if (isActiveChat) {
    className = "active-chat list-item";
  } else if (isInChat) {
    className = "joined-chat list-item";
  } else if (hasAgentChat) {
    className = "has-agent-chat list-item";
  } else if (isResolved) {
    className = "resolved-chat list-item";
  } else {
    className = "list-item";
  }

  if (hasUnreadMessages) {
    className += " notification-flash";
  }

  return className;
}

interface DisplayIdPayload {
  fullName: string;
  alias: string;
  name: string;
  mobileNo: string;
  email: string;
  chatId: string;
  channel: string;
  source: string;
  user_id: string;
  source_wa_id?: string;
}

/**
 * Gets channel-specific display ID for a chat.
 * Formats WhatsApp IDs with source_wa_id for multi-account scenarios.
 *
 * @param {DisplayIdPayload} payload - Chat identification data
 * @returns {string} Channel-specific display ID
 *
 * @example
 * getChannelSpecificDisplayId({ channel: 'whatsapp', user_id: '123', source_wa_id: 'wa1' });
 * // '123 @ wa1'
 */
export function getChannelSpecificDisplayId(payload: DisplayIdPayload): string {
  const { channel, source, user_id, source_wa_id } = payload;
  const finalChannelOrSource = channel || source;
  if (finalChannelOrSource) {
    if (finalChannelOrSource === "whatsapp") {
      if (source_wa_id) {
        return `${user_id} @ ${source_wa_id}`;
      }
      return user_id;
    }
  }
  return "";
}

/**
 * Gets display ID for a visitor with fallback hierarchy.
 * Priority: fullName > name > mobileNo > email > user_id > chatId > generated UUID.
 *
 * @param {DisplayIdPayload} payload - Visitor identification data
 * @returns {string} Display ID for the visitor
 *
 * @example
 * getDisplayId({ fullName: 'John Doe' }); // 'John Doe'
 * getDisplayId({ email: 'user@example.com' }); // 'user@example.com'
 */
export function getDisplayId(payload: DisplayIdPayload): string {
  const { fullName, name, mobileNo, email, user_id, chatId } = payload;
  const vistorId = uuid();
  if (fullName) {
    return fullName;
  }
  if (name) {
    return name;
  }
  if (mobileNo) {
    return mobileNo;
  }
  if (email) {
    return email;
  }
  if (user_id) {
    return user_id;
  }
  if (chatId) {
    return chatId;
  }
  return vistorId;
}

/**
 * Gets avatar URL for a chat visitor.
 *
 * @param {string} chatId - Chat identifier
 * @param {string} [profilePicture] - Profile picture URL if available
 * @returns {string} Avatar URL or empty string
 *
 * @example
 * getAvatar('chat123', 'https://example.com/avatar.jpg'); // Returns the URL
 */
export function getAvatar(chatId: string, profilePicture?: string) {
  if (profilePicture) {
    return profilePicture;
  }

  // TODO: remove once the old avatar is not needed
  // if (chatId) {
  //   // let pattern = GeoPattern.generate(chatId);
  //   // const result = pattern.toDataUrl();
  //   return chatId;
  // }
  return "";
}

// TODO: remove once the old avatar is not needed
// export function getAvatar(chatId: string, profilePicture?: string) {
//   if (profilePicture) {
//     return `url(${profilePicture})`;
//   }
//   if (chatId) {
//     let pattern = GeoPattern.generate(chatId);
//     const result = pattern.toDataUrl();
//     return result;
//   }
//   return "";
// }

/**
 * Extracts preview text from the latest user message in chat interactions.
 *
 * @param {InteractionType[]} chatInteractions - Array of chat messages
 * @returns {string} Preview text or "-" if no messages
 *
 * @example
 * const preview = getPreview(interactions); // "Hello, I need help"
 */
export function getPreview(chatInteractions: InteractionType[]) {
  if (!chatInteractions || chatInteractions.length === 0) {
    return "";
  }

  const userMessages = _.filter(chatInteractions, (interaction) => {
    const isUserMessage = isMessageByUser(interaction.type);
    return isUserMessage;
  });
  const latestUserMessage = _.first(userMessages);

  if (latestUserMessage && latestUserMessage.data && !Array.isArray(latestUserMessage.data)) {
    const messageTextFromContentObject = _.get(
      latestUserMessage,
      "data.content.text",
      "-"
    ) as string;
    const messageText = _.get(
      latestUserMessage,
      "data.content[0].text",
      messageTextFromContentObject
    ) as string;

    return messageText.trim();
  } else {
    return "-";
  }
}

/**
 * Checks if a message is from a user (not a bot reply).
 *
 * @param {string} messageType - Message type identifier
 * @returns {boolean} True if message is from user
 *
 * @example
 * isMessageByUser('message'); // true
 * isMessageByUser('reply'); // false
 */
export function isMessageByUser(messageType: string): boolean {
  const isUserMessage = messageType === "message" || messageType === "postback";
  return isUserMessage;
}

/**
 * Extracts unique tags from a list of chats, excluding system tags.
 * Filters out tags containing "entities", "RowKey", "PartitionKey", or "channel".
 *
 * @param {LivechatSessionType[]} chatList - Array of chat sessions
 * @returns {string[]} Array of unique user-defined tags
 *
 * @example
 * filterUniqueTags(chats); // ['vip', 'urgent', 'billing']
 */
export function filterUniqueTags(chatList: LivechatSessionType[]): string[] {
  const alltags = chatList.map((chat) => {
    const tags: string[] = _.get(chat, "tags", []);
    return tags;
  });
  const compactedTag = _.compact(_.flattenDeep(alltags)) as string[];

  const filteredTags = compactedTag.filter(
    (tag) =>
      !tag.includes("entities") &&
      !tag.includes("RowKey") &&
      !tag.includes("PartitionKey") &&
      !tag.includes("channel")
  );

  const finalTags = _.uniq(filteredTags);
  return finalTags;
}

/**
 * Converts UTC timestamp to local time format.
 *
 * @param {any} utc - UTC timestamp
 * @returns {string} Formatted local time string (DD/MM/YYYY hh:mm:ss A)
 *
 * @example
 * printLocaleTimeFromUtc('2024-01-01T00:00:00Z'); // '01/01/2024 08:00:00 AM'
 */
export function printLocaleTimeFromUtc(utc) {
  const momentUtc = moment.utc(utc);
  const localDate = moment(momentUtc).local();
  return moment(localDate).format("DD/MM/YYYY hh:mm:ss A");
}

/**
 * Formats a timestamp to local time string.
 *
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Formatted timestamp or "NA*" if null
 *
 * @example
 * printTimestamp('2024-01-01T00:00:00Z'); // '01/01/2024 08:00:00 AM'
 */
export function printTimestamp(timestamp: string): string {
  return timestamp != null ? moment(timestamp).format("DD/MM/YYYY hh:mm:ss A") : "NA*";
}

/**
 * Calculates and formats duration between two timestamps.
 *
 * @param {string} startTimestamp - Start time ISO string
 * @param {string} endTimestamp - End time ISO string
 * @param {boolean} [isSuffixNotIncluded=false] - Whether to exclude "ago" suffix
 * @returns {string} Human-readable duration string
 *
 * @example
 * printDuration(start, end); // '2 hours ago'
 * printDuration(start, end, true); // '2 hours'
 */
export function printDuration(
  startTimestamp: string,
  endTimestamp: string,
  isSuffixNotIncluded: boolean = false
): string {
  const isInvalidTimestamps = startTimestamp === "null" || endTimestamp === "null";
  if (isInvalidTimestamps) {
    return "-";
  }

  const start = moment(startTimestamp);
  const end = moment(endTimestamp);
  return start.from(end, isSuffixNotIncluded);
  // let duration = moment.duration(end.diff(start));
  // const finalDisplayDuration =
  //   Math.floor(duration.asMinutes()) + "m " + duration.seconds() + "s";
  // return finalDisplayDuration;
}

/**
 * Gets the appropriate chat array from store based on livechat type.
 *
 * @param {LivechatType} type - Livechat view type (queue, monitor, resolved, abandoned)
 * @returns {LivechatSessionType[]} Array of chat sessions for the specified type
 *
 * @example
 * const queueChats = getSelectedChatArrays('queue');
 */
export function getSelectedChatArrays(type: LivechatType): LivechatSessionType[] {
  const monitorChats = store.state.livechat.monitorChatsArray;
  const resolvedChats = store.state.livechat.resolvedChatsArray;
  const abandonedChats = store.state.livechat.abandonedChatsArray;
  let selectedChats = monitorChats;

  switch (type) {
    case "queue":
      {
        const queueChats = Object.values(store.state.livechat.queueChatObject);
        selectedChats = queueChats;
      }
      break;
    case "resolved":
      {
        selectedChats = resolvedChats;
      }
      break;
    case "abandoned":
      {
        selectedChats = abandonedChats;
      }
      break;
    case "monitor":
      selectedChats = monitorChats;
      break;
  }

  return selectedChats;
}

/**
 * Gets the appropriate chat collection from store based on livechat type.
 * Returns either object (queue) or array (resolved, abandoned, monitor).
 *
 * @param {LivechatType} type - Livechat view type
 * @returns {any} Chat collection (object or array)
 *
 * @example
 * const chats = getSelectedChatObject('queue');
 */
export function getSelectedChatObject(type: LivechatType) {
  let selectedChats;

  switch (type) {
    case "queue":
      {
        const queueChats = store.state.livechat.queueChatObject;
        selectedChats = queueChats;
      }
      break;
    case "resolved":
      {
        const resolvedChats = store.state.livechat.resolvedChatsArray;
        selectedChats = resolvedChats;
      }
      break;
    case "abandoned":
      {
        const abandonedChats = store.state.livechat.abandonedChatsArray;
        selectedChats = abandonedChats;
      }
      break;
    case "monitor":
    default:
      {
        const monitorChats = store.state.livechat.monitorChatsArray;
        selectedChats = monitorChats;
      }
      break;
  }

  return selectedChats;
}

/**
 * Gets the timestamp of the last message sent in a chat.
 * Compares interaction timestamp with chat last_modified time.
 *
 * @param {any} selectedChat - Chat session object
 * @param {InteractionType[]} interactions - Array of chat interactions
 * @returns {string} ISO timestamp string of last message
 *
 * @example
 * const lastTime = getLastSentMessageTimeStamp(chat, interactions);
 */
export function getLastSentMessageTimeStamp(
  selectedChat: any,
  interactions: InteractionType[]
): string {
  const orderedInteractions = _.orderBy(
    interactions,
    [
      (interaction) => {
        return moment(interaction.last_modified).valueOf();
      },
    ],
    ["desc"]
  );
  const latestUserInteraction = _.first(orderedInteractions);

  const dateCreatedTimeString = _.get(latestUserInteraction, "date_created");
  const lastModifiedTimeString = _.get(
    latestUserInteraction,
    "last_modified",
    dateCreatedTimeString
  );
  let lastModifiedTime = moment(lastModifiedTimeString);
  const chatLastModifiedTime = moment(selectedChat.last_modified);

  if (chatLastModifiedTime.isAfter(lastModifiedTime)) {
    lastModifiedTime = chatLastModifiedTime;
  }
  return lastModifiedTime.toISOString();
}

/**
 * Gets human-readable time since last user message.
 * Excludes bot replies from consideration.
 *
 * @param {InteractionType[]} interactions - Array of chat interactions
 * @returns {string} Relative time string (e.g., "2 hours ago")
 *
 * @example
 * getLastSentMessageTime(interactions); // '5 minutes ago'
 */
export function getLastSentMessageTime(interactions: InteractionType[]): string {
  const userMessages = _.filter(interactions, (interaction) => interaction.type !== "reply");
  const orderedInteractions = _.orderBy(
    userMessages,
    [
      (interaction) => {
        return moment(interaction.date_created).valueOf();
      },
    ],
    ["desc"]
  );
  const latestUserInteraction = _.first(orderedInteractions);

  const timestamp = _.get(latestUserInteraction, "date_created");
  const timeMessage = moment(timestamp).fromNow();

  return timeMessage;
}

/**
 * Detects if browser is Internet Explorer or Edge (legacy).
 *
 * @returns {boolean} True if IE or Edge browser
 *
 * @example
 * if (isIEOrEdge()) {
 *   console.log('Using legacy browser');
 * }
 */
export function isIEOrEdge() {
  if (!navigator) return false;
  let _ieOrEdge = false;
  if (/MSIE 10/i.test(navigator.userAgent)) {
    // This is internet explorer 10
    _ieOrEdge = true;
  }
  if (/MSIE 9/i.test(navigator.userAgent) || /rv:11.0/i.test(navigator.userAgent)) {
    // This is internet explorer 9 or 11
    _ieOrEdge = true;
  }
  if (/Edge\/\d./i.test(navigator.userAgent)) {
    // This is Microsoft Edge
    _ieOrEdge = true;
  }
  return _ieOrEdge;
}

/**
 * Checks if PII-redacted text should be transferred during chat handover.
 * Returns true if PII is enabled and dontTransferRedacted is false.
 *
 * @returns {boolean} True if redacted text should be transferred
 *
 * @example
 * if (isTransferRedactedText()) {
 *   transferRedactedMessages();
 * }
 */
export function isTransferRedactedText(): boolean {
  const enabled = _.get(store, "state.modules.pii.enabled", false);
  const transferRedacted = !_.get(store, "state.modules.pii.dontTransferRedacted", false);
  const result = enabled && transferRedacted;
  return result;
}
